import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { MDSComboboxVariant } from "./combobox.types";
import { MDSPopover } from "@/mds/components/overlays";

export const StyledCombobox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  position: relative;
  width: 100%;
`;

export const StyledLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
`;

export const StyledInputContainer = styled.div<{
  variant?: MDSComboboxVariant;
  hasLeftActionButton?: boolean;
  $isExpanded?: boolean;
  $hasMentions?: boolean;
}>`
  display: flex;
  position: relative;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme, variant, hasLeftActionButton }) =>
    getPadding(theme, variant, hasLeftActionButton)};
  border: 1px solid ${({ theme, variant }) => getBorderColor(theme, variant)};
  border-radius: ${({ theme, variant, $isExpanded, $hasMentions }) =>
    getBorderRadius(theme, variant, $isExpanded, $hasMentions)};
  background: ${({ theme, variant }) => getBackgroundColor(theme, variant)};
  min-height: 32px;
  align-items: ${({ $isExpanded }) => ($isExpanded ? "auto" : "center")};
  transition:
    border-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    background 180ms cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 180ms cubic-bezier(0.4, 0, 0.2, 1);
  cursor: text;

  &:focus-within {
    border-color: ${({ theme, variant }) => getFocusBorderColor(theme, variant)};
    background: ${({ theme, variant }) => getFocusBackgroundColor(theme, variant)};
  }

  &:hover:not(:focus-within):not([aria-disabled="true"]) {
    border-color: ${({ theme, variant }) => getHoverBorderColor(theme, variant)};
  }

  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const StyledLeftContainer = styled.div`
  display: flex;
  align-items: flex-start;

  min-height: 32px;

  &:empty {
    display: none;
  }
`;

export const StyledRightContainer = styled.div<{
  $variant?: MDSComboboxVariant;
}>`
  display: flex;
  align-items: flex-center;
  min-height: 32px;

  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-left: auto;
  margin-right: ${({ theme }) => theme.spacing["1"]};

  & svg {
    height: ${({ theme }) => theme.spacing["8"]};
    width: ${({ theme }) => theme.spacing["8"]};
  }
`;

export const StyledLeftIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > div {
    height: 16px;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.icon.secondary};
  }
`;

export const StyledLeftAddonContainer = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  /*
   * Instantly hide and collapse width on expand (to avoid collision with input).
   * Animate back on collapse.
   */
  visibility: ${({ $isExpanded }) => ($isExpanded ? "hidden" : "visible")};
  opacity: ${({ $isExpanded }) => ($isExpanded ? 0 : 1)};
  width: ${({ $isExpanded }) => ($isExpanded ? 0 : "auto")};
  overflow: hidden;
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? "none" : "auto")};
  transition: ${({ $isExpanded }) =>
    $isExpanded
      ? "opacity 0s, visibility 0s, width 0s"
      : "opacity 180ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s, width 180ms cubic-bezier(0.4, 0, 0.2, 1)"};
`;

export const StyledInputArea = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0; /* Allow shrinking */
  min-height: 32px;

  display: flex;
  align-items: center;
  flex-direction: column;
`;

export const StyledExpand = styled.div`
  flex: 1;
  min-width: 0; /* Allow shrinking */
  width: 100%;
  margin-left: ${({ theme }) => theme.spacing["1"]};
  margin-top: ${({ theme }) => theme.spacing["0.5"]};

  /* Constrain the editor content */
  .ProseMirror {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    outline: none;
    border: none;
    background: transparent;
    padding: 2px 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
    line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
    text-align: left;
    cursor: text;
    color: ${({ theme }) => theme.colors.text.primary};

    /* Prevent horizontal scrolling */
    overflow-x: hidden;

    /* Allow multi-line but limit height with scrolling */
    max-height: 120px;
    overflow-y: auto;

    /* Hide scrollbar */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    & [data-placeholder] {
      color: ${({ theme }) => theme.colors.text.tertiary};
    }
  }

  /* Style the MemCommonRichTextInput container directly */
  > div {
    width: 100%;
    min-width: 0;
  }
`;

export const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  color: ${({ theme }) => theme.colors.text.danger};
  margin-top: ${({ theme }) => theme.spacing["1"]};
`;

// Helper functions for border colors
const getBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.border.input.brand.default;
    case MDSComboboxVariant.Chrome:
      return theme.colors.border.input.default.default;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.border.input.default.default;
    default:
      return theme.colors.border.input.default.default;
  }
};

const getFocusBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.border.input.brand.focused;
    case MDSComboboxVariant.Chrome:
      return theme.colors.border.input.default.focused;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.border.input.emphasis.focused;
    default:
      return theme.colors.border.input.default.focused;
  }
};

const getBackgroundColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.background.input.onAccent.default;
    case MDSComboboxVariant.Chrome:
      return theme.colors.background.input.chrome.default;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.background.input.default.default;
    default:
      return theme.colors.background.input.default.default;
  }
};

const getFocusBackgroundColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return theme.colors.background.input.onAccent.focused;
    case MDSComboboxVariant.Chrome:
      return theme.colors.background.input.chrome.focused;
    case MDSComboboxVariant.Emphasis:
      return theme.colors.background.input.default.focused;
    default:
      return theme.colors.background.input.default.focused;
  }
};

const getHoverBorderColor = (theme: Theme, variant?: MDSComboboxVariant) => {
  return getFocusBorderColor(theme, variant);
};

const getBorderRadius = (
  theme: Theme,
  variant?: MDSComboboxVariant,
  isExpanded?: boolean,
  hasMentions?: boolean
) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
    case MDSComboboxVariant.Chrome:
    case MDSComboboxVariant.Emphasis:
      return isExpanded || hasMentions ? theme.borderRadius.xlarge : theme.borderRadius.max;
    default:
      return theme.borderRadius.large;
  }
};

const getPadding = (theme: Theme, variant?: MDSComboboxVariant, hasLeftActionButton?: boolean) => {
  switch (variant) {
    case MDSComboboxVariant.Brand:
      return `${theme.spacing[3]} ${theme.spacing[2]} ${theme.spacing[3]} ${hasLeftActionButton ? theme.spacing[3] : theme.spacing[5]}`;
    case MDSComboboxVariant.Emphasis:
      return `0 ${theme.spacing[5]} 0`;
    case MDSComboboxVariant.Chrome:
      return `0 ${theme.spacing[5]}`;
    default:
      return `${theme.spacing[1]} ${theme.spacing[5]}`;
  }
};

// Custom popover for combobox that removes default padding
export const StyledComboboxPopover = styled(MDSPopover)`
  padding: 0;
  background: ${({ theme }) => theme.colors.surface.menu};
`;

export const StyledPopoverContent = styled.div`
  box-sizing: border-box;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing["1"]};
  background: ${({ theme }) => theme.colors.surface.menu};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  width: var(--combobox-popover-trigger-width);
  min-width: var(--combobox-popover-trigger-width);
`;

export const StyledEmptyResult = styled.div`
  padding: 1rem;
  text-align: left;
`;

export const StyledPopoverSearchContainer = styled.div`
  padding: ${({ theme }) => theme.spacing["1"]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  margin-bottom: ${({ theme }) => theme.spacing["1"]};
`;

export const StyledBottomRow = styled.div<{ $isExpanded?: boolean }>`
  width: 100%;
  overflow: hidden;
  margin-left: -6px;

  /* Animate height using grid for smooth animation */
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? "1fr" : "0fr")};
  transition: grid-template-rows 180ms cubic-bezier(0.4, 0, 0.2, 1);

  /* Ensure child can collapse */
  > * {
    overflow: hidden;
  }
`;

export const StyledBottomRowInner = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 0; /* Required for grid animation to work */
  padding-top: ${({ theme }) => theme.spacing["1"]};

  /*
   * Use visibility to instantly hide content when collapsing.
   * This prevents the "rearranging" visual artifact during height animation.
   * - Expand: content becomes visible instantly, then fades in
   * - Collapse: content hides instantly (no animation)
   */
  visibility: ${({ $isExpanded }) => ($isExpanded ? "visible" : "hidden")};
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  transition: ${({ $isExpanded }) =>
    $isExpanded
      ? `opacity 120ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s`
      : `opacity 0s, visibility 0s`};

  /* Inner content sizing */
  > * {
    flex-shrink: 0;
  }
`;

export const StyledBottomLeftContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const StyledBottomRightContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
